export enum TaskStatus {
  PENDING = 'PENDING',
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  DATA_COLLECTED = 'DATA_COLLECTED',
  VALIDATION = 'VALIDATION',
  EMAIL_DISCOVERY = 'EMAIL_DISCOVERY',
  NORMALIZATION = 'NORMALIZATION',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface State {
  id: number;
  name: string;
}

export interface City {
  id: number;
  name: string;
  state: State;
  lat: number;
  lng: number;
  lat_start?: number;
  long_start?: number;
  lat_end?: number;
  long_end?: number;
  display_name: string;
  state_name: string;
  country: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  created_at: string;
}

export interface SearchProgressPipeline {
  variant: 'regular' | 'smart-filter' | 'no-website';
  step_ids: string[];
  active_step_id: string | null;
}

export interface Search {
  id: number;
  search_query: string;
  city_name: string;
  state_name: string;
  country: string;
  lat: number;
  lng: number;
  radius?: number;
  status: TaskStatus;
  total_points: number;
  processed_points: number;
  completed_links: number;
  total_links: number;
  output_file: string | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  user_id?: number;
  progress: number;
  progress_pipeline?: SearchProgressPipeline | null;
  // For backwards compatibility
  center_lat?: number;
  center_lon?: number;
  population?: number;
  // New nested city object
  city?: City;
  user?: User;
  failed_links?: number;
  places_saved?: number;
  address_normalization_status?: string;
  total_places_for_normalization?: number;
  normalized_places_count?: number;
  website_scraping_status?: string;
  total_places_for_website_scraping?: number;
  website_scraped_places_count?: number;
  page_checker_status?: string;
  total_pages_to_check?: number;
  checked_pages_count?: number;
  is_queued?: boolean;
  queue_position?: number;
  estimated_wait_minutes?: number;
  google_maps_requirements?: GoogleMapsRequirements | null;
  google_ads_signal?: 'detected_recently' | 'no_ad_tracking' | null;
  google_ads_detected_count?: number | null;
  website_filter?: {
    enabled: boolean;
    description: string;
    interpretation: string;
    offering_tags: Record<string, string>;
    customer_tags: string[];
    accepted_warnings: string[];
    processing_status: string;
  } | null;
}

export interface GoogleMapsRequirements {
  min_review_count?: number | null;
  max_review_count?: number | null;
  min_review_rating?: number | null;
  max_review_rating?: number | null;
  has_phone_number?: boolean | null;
  has_website?: boolean | null;
  temporary_closed?: boolean | null;
  lgbtq_friendly?: boolean | null;
  latino_owned?: boolean | null;
  asian_owned?: boolean | null;
  veteran_owned?: boolean | null;
}

export interface CreateSearchParams {
  search_query: string;
  city_name: string;
  state_name: string;
  country: string;
  lat?: number;
  lng?: number;
  lat_start?: number;
  long_start?: number;
  lat_end?: number;
  long_end?: number;
  radius?: number;
  // Search options
  basic_business_info?: boolean;
  business_categories?: boolean;
  websites?: boolean;
  business_emails?: boolean;
  social_media_links?: boolean;
  decision_makers?: boolean;
  email_enrichment?: boolean;
  google_maps_requirements?: GoogleMapsRequirements | null;
  category_input?: {
    is_custom: boolean;
    resolved_categories: string[];
    resolution_status: string;
    resolution_confidence?: number;
    explanation?: string;
  };
  website_filter?: ApprovedWebsiteFilter;
  google_ads_signal?: 'detected_recently' | 'no_ad_tracking' | null;
}

export interface ApprovedWebsiteFilter {
  enabled: boolean;
  description: string;
  interpretation: string;
  offerings: string[];
  short_offerings: string[];
  customers: string[];
  warnings: string[];
}

export interface FilterInterpretation {
  status: 'approved' | 'warning' | 'rejected';
  offerings: string[];
  short_offerings: string[];
  customers: string[];
  warnings: string[];
  reason: string;
  suggested_rewrite: string;
  interpretation: string;
}

export interface SearchLimitError {
  message: string;
  active_searches: number;
  max_allowed: number;
  current_plan: string;
  limit_type?: 'active_searches' | string;
  suggested_action?: string;
}

export interface SearchState {
  searches: Search[];
  currentSearch: Search | null;
  loading: boolean;
  error: string | SearchLimitError | null;
}

export interface PaginationResponse<T> {
  items: T[];
  page: number;
  total_pages: number;
  total_items: number;
  items_per_page: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface SearchListResponse extends PaginationResponse<Search> {
  total_completed: number;
  total_processing: number;
  total_failed: number;
}

export interface SearchListParams {
  page?: number;
  items_per_page?: number;
  status?: string;
} 
